import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Stack,
  CircularProgress
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import GoogleMap from '../GoogleMap';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';

const ContactContent: React.FC = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'General Inquiry',
    message: ''
  });

  const [errors, setErrors] = useState({
    name: false,
    email: false,
    message: false
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  const handleSubjectChange = (e: SelectChangeEvent) => {
    setFormData(prev => ({ ...prev, subject: e.target.value }));
  };

  const validateForm = () => {
    const newErrors = {
      name: formData.name.trim() === '',
      email: !/^\S+@\S+\.\S+$/.test(formData.email.trim()),
      message: formData.message.trim() === ''
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const functions = getFunctions();
      const sendContactEmail = httpsCallable(functions, 'sendContactEmail');
      await sendContactEmail({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message
      });

      setSnackbar({ open: true, message: t('contact.successMessage'), severity: 'success' });
      setFormData({ name: '', email: '', phone: '', subject: 'General Inquiry', message: '' });
    } catch (error) {
      setSnackbar({ open: true, message: t('contact.errorMessage'), severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ py: 8 }}>
      <Container>
        <Typography variant="h2" component="h1" align="center" gutterBottom>
          {t('contact.title')}
        </Typography>
        <Typography
          variant="h6"
          component="p"
          align="center"
          color="text.secondary"
          sx={{ mb: 6, maxWidth: 800, mx: 'auto' }}
        >
          {t('contact.subtitle')}
        </Typography>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={6}>
          {/* Contact Info */}
          <Box sx={{ width: { xs: '100%', md: '40%' } }}>
            <Paper
              elevation={0}
              sx={{ p: 4, height: '100%', bgcolor: 'primary.light', borderRadius: 2 }}
            >
              <Typography variant="h5" component="h2" gutterBottom>
                Desiree African Hair Braiding
              </Typography>

              <Box sx={{ mt: 4 }}>
                <Box sx={{ display: 'flex', mb: 3 }}>
                  <LocationIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h6" component="h3">
                      {t('contact.address')}
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {t('contact.addressValue')}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', mb: 3 }}>
                  <PhoneIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h6" component="h3">
                      {t('contact.phone')}
                    </Typography>
                    <Typography variant="body1">
                      {t('contact.phoneValue')}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', mb: 3 }}>
                  <EmailIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h6" component="h3">
                      {t('contact.email')}
                    </Typography>
                    <Typography variant="body1">
                      {t('contact.emailValue')}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex' }}>
                  <TimeIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h6" component="h3">
                      {t('contact.hours')}
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {t('contact.hoursValue')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Contact Form */}
          <Box sx={{ width: { xs: '100%', md: '60%' } }}>
            <Paper
              elevation={0}
              sx={{ p: 4, borderRadius: 2, boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)' }}
            >
              <Typography variant="h5" component="h2" gutterBottom>
                {t('contact.sendMessage')}
              </Typography>

              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                      <TextField
                        fullWidth
                        label={t('contact.name')}
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        error={errors.name}
                        helperText={errors.name ? t('contact.nameRequired') : ''}
                        required
                      />
                    </Box>
                    <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                      <TextField
                        fullWidth
                        label={t('contact.emailField')}
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        error={errors.email}
                        helperText={errors.email ? t('contact.emailRequired') : ''}
                        required
                      />
                    </Box>
                  </Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                      <TextField
                        fullWidth
                        label={t('contact.phoneField')}
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </Box>
                    <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                      <FormControl fullWidth>
                        <InputLabel id="subject-label">{t('contact.subject')}</InputLabel>
                        <Select
                          labelId="subject-label"
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          label={t('contact.subject')}
                          onChange={handleSubjectChange}
                        >
                          <MenuItem value="General Inquiry">{t('contact.subjects.general')}</MenuItem>
                          <MenuItem value="Booking Question">{t('contact.subjects.booking')}</MenuItem>
                          <MenuItem value="Service Information">{t('contact.subjects.service')}</MenuItem>
                          <MenuItem value="Feedback">{t('contact.subjects.feedback')}</MenuItem>
                          <MenuItem value="Other">{t('contact.subjects.other')}</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Stack>

                  <Box>
                    <TextField
                      fullWidth
                      label={t('contact.message')}
                      name="message"
                      multiline
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      error={errors.message}
                      helperText={errors.message ? t('contact.messageRequired') : ''}
                      required
                    />
                  </Box>
                </Stack>

                <Box sx={{ position: 'relative', mt: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={isSubmitting}
                    sx={{ color: 'text.primary' }}
                  >
                    {t('contact.send')}
                  </Button>
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-12px',
                        marginLeft: '-12px',
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Paper>
          </Box>
        </Stack>

        <Box sx={{ mt: 8 }}>
          <Typography variant="h4" component="h2" align="center" gutterBottom>
            {t('contact.findUs')}
          </Typography>
          <GoogleMap height={400} />
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default ContactContent;
